import { UNITS, unitLabel, type Unit, type UnitDimension } from './units.ts';

/** The amount part of an ingredient line. */
export interface Amount {
  quantity: number | null;
  quantityMax?: number | null;
  unit: Unit | null;
}

export type DisplayLocale = 'de' | 'en';

/** Quantity in its dimension's base unit (g, ml; count units stay as they are). */
export function toBase(quantity: number, unit: Unit | null): number {
  return unit ? quantity * UNITS[unit].factor : quantity;
}

/** Converts between units of the same mass or volume dimension; null otherwise. */
export function convert(quantity: number, from: Unit, to: Unit): number | null {
  if (from === to) return quantity;
  const a = UNITS[from];
  const b = UNITS[to];
  if (a.dimension !== b.dimension || (a.dimension !== 'mass' && a.dimension !== 'volume')) {
    return null;
  }
  return (quantity * a.factor) / b.factor;
}

export function sameDimension(a: Unit | null, b: Unit | null): boolean {
  const dim = (u: Unit | null): UnitDimension => (u ? UNITS[u].dimension : 'count');
  if (dim(a) !== dim(b)) return false;
  // Count units only merge with themselves ("2 Dosen" + "3 Stück" are different things).
  return dim(a) === 'mass' || dim(a) === 'volume' || a === b;
}

/** Scales linearly from the recipe yield (CONTEXT.md: Yield). */
export function scaleAmount<T extends Amount>(amount: T, factor: number): T {
  return {
    ...amount,
    quantity: amount.quantity === null ? null : amount.quantity * factor,
    quantityMax:
      amount.quantityMax === null || amount.quantityMax === undefined
        ? null
        : amount.quantityMax * factor,
  };
}

export const scaleFactor = (yieldPortions: number, portions: number) =>
  yieldPortions > 0 ? portions / yieldPortions : 1;

const FRACTIONS: [number, string][] = [
  [1 / 8, '⅛'],
  [1 / 4, '¼'],
  [1 / 3, '⅓'],
  [1 / 2, '½'],
  [2 / 3, '⅔'],
  [3 / 4, '¾'],
];

/** Units measured in spoons, cups and pieces read best as fractions (⅓ EL, 1 ½ Tassen). */
const FRACTIONAL = new Set<Unit | null>([
  null,
  'tsp',
  'tbsp',
  'cup',
  'can',
  'jar',
  'pack',
  'pot',
  'bottle',
  'bag',
  'bunch',
  'piece',
  'clove',
  'slice',
  'handful',
  'sprig',
  'stalk',
  'leaf',
  'head',
  'cube',
  'pinch',
  'knifetip',
  'dash',
]);

function decimal(value: number, maxDigits: number, locale: DisplayLocale): string {
  return new Intl.NumberFormat(locale === 'de' ? 'de-DE' : 'en-GB', {
    maximumFractionDigits: maxDigits,
    useGrouping: value >= 10_000,
  }).format(value);
}

/** `⅓`, `1 ½`, `2`; falls back to one decimal when no fraction is close. */
function fraction(value: number, locale: DisplayLocale): string {
  const whole = Math.floor(value + 1e-9);
  const rest = value - whole;
  if (rest < 0.06) return String(whole === 0 && value > 0 ? decimal(value, 2, locale) : whole);
  if (rest > 0.94) return String(whole + 1);
  for (const [f, glyph] of FRACTIONS) {
    // ⅛ only when it's really meant; the others absorb rounding (0.33 → ⅓).
    if (Math.abs(rest - f) < (f < 0.2 ? 0.015 : 0.04))
      return whole === 0 ? glyph : `${whole} ${glyph}`;
  }
  return decimal(value, 1, locale);
}

/** Metric amounts: 7,5 g · 250 g · 1,25 kg; the larger unit from 1000 up. */
function metric(base: number, dimension: 'mass' | 'volume', locale: DisplayLocale) {
  const [small, large] = dimension === 'mass' ? (['g', 'kg'] as const) : (['ml', 'l'] as const);
  if (base >= 1000) return { value: decimal(base / 1000, 2, locale), unit: large };
  if (base >= 100) return { value: decimal(Math.round(base / 5) * 5, 0, locale), unit: small };
  if (base >= 10) return { value: decimal(Math.round(base), 0, locale), unit: small };
  return { value: decimal(base, 1, locale), unit: small };
}

/**
 * Display string for an amount (DESIGN.md §3: metric, locale formats, no false precision):
 * `⅓ EL`, `1 ½ Tassen`, `1,25 kg`, `2–3`, `500 g` for 0.5 kg; oz/lb/fl oz shown metric.
 * Empty string when there is no quantity.
 */
export function formatAmount(amount: Amount, locale: DisplayLocale = 'de'): string {
  const { quantity, unit } = amount;
  const max = amount.quantityMax ?? null;
  if (quantity === null) return unit ? unitLabel(unit, locale, 1) : '';

  const dimension = unit ? UNITS[unit].dimension : 'count';
  const isMetric =
    unit !== null &&
    (dimension === 'mass' || dimension === 'volume') &&
    unit !== 'cup' &&
    unit !== 'tsp' &&
    unit !== 'tbsp';
  if (isMetric) {
    const lo = metric(toBase(quantity, unit), dimension, locale);
    if (max === null) return `${lo.value} ${lo.unit}`;
    const hi = metric(toBase(max, unit), dimension, locale);
    return lo.unit === hi.unit
      ? `${lo.value}–${hi.value} ${hi.unit}`
      : `${lo.value} ${lo.unit} – ${hi.value} ${hi.unit}`;
  }

  const number = (n: number) =>
    FRACTIONAL.has(unit) ? fraction(n, locale) : decimal(n, n < 10 ? 1 : 0, locale);
  const value = max === null ? number(quantity) : `${number(quantity)}–${number(max)}`;
  return unit ? `${value} ${unitLabel(unit, locale, max ?? quantity)}` : value;
}
